import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, CheckSquare, Clock, Briefcase } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/layout/PageHeader";
import { KpiCard } from "../components/dashboard/KpiCard";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import Timeline from "../components/activity/Timeline";
import StatusPill from "../components/status/StatusPill";
import { useTheme } from "../lib/theme-context";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  DEAL_STAGE_LABELS,
  DEAL_STAGE_ORDER,
  normalizeDealStageForWorkflow,
  TASK_STATUS_LABELS,
  PRIORITY_LABELS,
} from "../lib/labels";
import { formatDate } from "../lib/format";
import { useAuth } from "../lib/auth-context";
import { buildDashboardLeadStatusRows } from "../lib/dashboard-lead-status";

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const pipelineChartRef = useRef(null);
  const [pipelineChartWidth, setPipelineChartWidth] = useState(0);
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const { isDark } = useTheme();

  useEffect(() => {
    api
      .get("/api/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(extractApiError(e, "Dashboard konnte nicht geladen werden.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    const node = pipelineChartRef.current;
    if (!node) return;

    const setFromWidth = (width) => {
      const next = Math.max(1, Math.floor(width));
      setPipelineChartWidth((prev) => (prev === next ? prev : next));
    };

    const measure = () => {
      const rect = node.getBoundingClientRect();
      setFromWidth(rect.width);
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width;
        if (typeof w === "number") setFromWidth(w);
      });
      ro.observe(node);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [loading]);

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground py-12 text-center">
        Dashboard wird geladen …
      </div>
    );
  }
  if (!data) return null;

  const greeting = getGreeting();
  const firstName = user?.full_name?.split(" ")[0] || "";

  const stageData = DEAL_STAGE_ORDER.map((s) => ({
    stage: DEAL_STAGE_LABELS[s] || s,
    value: getVisibleDealStageCount(data.kpis.deals_by_stage || {}, s),
  }));
  const statusData = buildDashboardLeadStatusRows(data.kpis.leads_by_status || {}, {
    convertedCount: data.kpis.converted_leads_count || 0,
  });

  const axisColor = isDark ? "#a1a1aa" : "#71717a";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${greeting}${firstName ? `, ${firstName}` : ""}`}
        description={
          <span>
            Überblick über Leads, Aufgaben und Pipeline –{" "}
            <span className="text-foreground/80">{tenant?.name}</span>
          </span>
        }
      />

      {/* Bento KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Neue Leads"
          value={data.kpis.new_leads_count}
          icon={Inbox}
          testId="kpi-new-leads"
          onClick={() => navigate("/leads")}
          accent
        />
        <KpiCard
          label="Offene Aufgaben"
          value={data.kpis.open_tasks_count}
          icon={CheckSquare}
          testId="kpi-open-tasks"
          onClick={() => navigate("/aufgaben")}
        />
        <KpiCard
          label="Überfällig"
          value={data.kpis.overdue_tasks_count}
          icon={Clock}
          testId="kpi-overdue-tasks"
          onClick={() => navigate("/aufgaben")}
        />
        <KpiCard
          label="Deals gesamt"
          value={data.kpis.deals_total}
          icon={Briefcase}
          testId="kpi-deals-total"
          onClick={() => navigate("/pipeline")}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Pipeline chart */}
        <Card className="lg:col-span-8" data-testid="dashboard-pipeline-chart">
          <CardHeader>
            <CardTitle>Pipelineübersicht</CardTitle>
            <div className="text-xs text-muted-foreground">Deals nach Deal-Phase</div>
          </CardHeader>
          <CardContent>
            <div ref={pipelineChartRef} className="h-72 w-full min-w-0">
              {pipelineChartWidth > 0 ? (
                <BarChart
                  width={pipelineChartWidth}
                  height={288}
                  data={stageData}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="brandBarFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.95} />
                      <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="stage"
                    tick={{ fontSize: 11, fill: axisColor }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={70}
                    axisLine={{ stroke: gridColor }}
                    tickLine={{ stroke: gridColor }}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: axisColor }}
                    axisLine={{ stroke: gridColor }}
                    tickLine={{ stroke: gridColor }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                      color: "hsl(var(--foreground))",
                    }}
                    cursor={{ fill: gridColor }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="url(#brandBarFill)" />
                </BarChart>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Lead status panel */}
        <Card className="lg:col-span-4" data-testid="dashboard-lead-status-chart">
          <CardHeader>
            <CardTitle>Lead-Status</CardTitle>
            <div className="text-xs text-muted-foreground">Verteilung im aktuellen Mandanten</div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {statusData.map((row) => (
                <li
                  key={row.key}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm rounded-lg px-2 py-1.5 hover:bg-muted/50 transition-colors"
                >
                  <StatusPill tone={row.tone}>{row.label}</StatusPill>
                  <span className="tabular-nums font-medium">{row.value}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Recent activities */}
        <Card className="lg:col-span-7" data-testid="dashboard-recent-activities">
          <CardHeader>
            <CardTitle>Letzte Aktivitäten</CardTitle>
            <div className="text-xs text-muted-foreground">Chronologischer Verlauf</div>
          </CardHeader>
          <CardContent>
            <Timeline items={data.recent_activities} />
          </CardContent>
        </Card>

        {/* Upcoming tasks */}
        <Card className="lg:col-span-5" data-testid="dashboard-upcoming-tasks">
          <CardHeader>
            <CardTitle>Nächste Aufgaben</CardTitle>
            <div className="text-xs text-muted-foreground">Offene und in Bearbeitung</div>
          </CardHeader>
          <CardContent>
            {data.upcoming_tasks.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                Keine offenen Aufgaben.
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {data.upcoming_tasks.map((t) => {
                  const st = TASK_STATUS_LABELS[t.status] || { de: t.status, tone: "muted" };
                  const pr = PRIORITY_LABELS[t.priority] || { de: t.priority, tone: "muted" };
                  return (
                    <li
                      key={t.id}
                      className="py-2.5 flex flex-wrap items-start justify-between gap-3 hover:bg-muted/30 -mx-2 px-2 rounded-md transition-colors"
                    >
                      <div className="min-w-0">
                        <button
                          type="button"
                          className="text-left text-sm font-medium break-words hover:underline"
                          onClick={() => navigate(`/aufgaben/${t.id}`)}
                          data-testid={`dashboard-task-link-${t.id}`}
                        >
                          {t.title}
                        </button>
                        <div className="text-xs text-muted-foreground">
                          {t.due_date ? `Fällig ${formatDate(t.due_date)}` : "Kein Fälligkeitsdatum"}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                        <StatusPill tone={pr.tone}>{pr.de}</StatusPill>
                        <StatusPill tone={st.tone}>{st.de}</StatusPill>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 5) return "Gute Nacht";
  if (h < 11) return "Guten Morgen";
  if (h < 18) return "Hallo";
  return "Guten Abend";
}

function getVisibleDealStageCount(stageCounts, stage) {
  const normalizedStage = normalizeDealStageForWorkflow(stage);
  return Object.entries(stageCounts).reduce((sum, [key, value]) => {
    return normalizeDealStageForWorkflow(key) === normalizedStage ? sum + (value || 0) : sum;
  }, 0);
}
